import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select } from "antd";
import {
  courseLabel,
  ResolvedCourse,
  searchCoursesForPicker,
  toResolvedCourse,
} from "./quizCourseAssignment";

// Controlled Form control: the Form field holds the whole course, so the label
// survives a search whose results no longer include it.
type Props = {
  value?: ResolvedCourse | null;
  onChange?: (course: ResolvedCourse | null) => void;
};

const SEARCH_DELAY_MS = 350;
const PICKER_LIMIT = 20;

const CourseAssignmentSelect: React.FC<Props> = ({ value, onChange }) => {
  const [results, setResults] = useState<ResolvedCourse[]>([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Only the latest search may set the results.
  const searchSeq = useRef(0);

  useEffect(
    () => () => {
      clearTimeout(timer.current);
      searchSeq.current += 1;
    },
    [],
  );

  const handleSearch = (text: string) => {
    clearTimeout(timer.current);
    const seq = (searchSeq.current += 1);
    const query = text.trim();
    if (!query) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    timer.current = setTimeout(() => {
      searchCoursesForPicker(query, PICKER_LIMIT)
        .then((courses) => {
          if (seq === searchSeq.current)
            setResults(courses.map(toResolvedCourse));
        })
        .catch(() => {
          if (seq === searchSeq.current) setResults([]);
        })
        .finally(() => {
          if (seq === searchSeq.current) setSearching(false);
        });
    }, SEARCH_DELAY_MS);
  };

  // The selected course always stays among the options so its label shows.
  const courses = useMemo(
    () =>
      value ? [value, ...results.filter((c) => c.id !== value.id)] : results,
    [value, results],
  );

  return (
    <Select
      showSearch
      allowClear
      filterOption={false}
      placeholder="Buscá el curso por ID, título o clave"
      value={value?.id}
      loading={searching}
      notFoundContent={searching ? "Buscando..." : "Sin resultados"}
      onSearch={handleSearch}
      onChange={(id: number | undefined) =>
        onChange?.(courses.find((c) => c.id === id) ?? null)
      }
      options={courses.map((c) => ({ value: c.id, label: courseLabel(c) }))}
    />
  );
};

export default CourseAssignmentSelect;
